import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { ReviewCategory } from "../../types";
import { useUiState } from "../../state";
import { textMap } from "../../i18n";
import {
  ApiError,
  fetchStudioScanDetail,
  getScanProgressStepLabels,
  patchReviewTaskSeries,
  scanClaims,
} from "../../services/compliance";
import { StudioScanResultDetail } from "./StudioScanResultDetail";
import {
  appendStudioScan,
  getStudioScanById,
  newScanDisplayId,
  upsertStudioScan,
  type StoredStudioScan,
} from "./studioScanHistoryStorage";
import {
  isBackendTaskId,
  storedScanFromDetailDto,
} from "./studioServerScan";
import { verticalToCategory, type VerticalKey } from "./studioShared";
import { Modal, Spin, message } from "antd";
import { ScanProgressModal } from "../../components/ScanProgressModal";
import { useReviewCredits } from "../../hooks/useReviewCredits";
import "../../styles/studio-workbench.css";

type StudioDetailLocationState = { studioDetailBack?: "new" | "records" };

export function StudioScanDetailPage() {
  const { scanId } = useParams<{ scanId: string }>();
  const decodedId = scanId ? decodeURIComponent(scanId) : "";
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { lang, themeMode, toggleLang, toggleTheme } = useUiState();
  const t = textMap[lang];
  const isDark = themeMode === "dark";
  const {
    reviewCredits,
    billingReady,
    isUnlimited,
    applyFromScanResult,
  } = useReviewCredits();
  const [historyVersion, setHistoryVersion] = useState(0);
  const [record, setRecord] = useState<StoredStudioScan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!decodedId) {
      setRecord(null);
      setLoading(false);
      return;
    }
    let cancel = false;
    async function run() {
      setLoading(true);
      try {
        if (isBackendTaskId(decodedId)) {
          const cached = getStudioScanById(decodedId);
          if (cached?.finalizedAt != null) {
            if (!cancel) setRecord(cached);
            return;
          }
          try {
            const d = await fetchStudioScanDetail(decodedId);
            if (cancel) return;
            const row = storedScanFromDetailDto(d);
            const merged =
              cached?.finalizedAt != null
                ? { ...row, finalizedAt: cached.finalizedAt }
                : row;
            upsertStudioScan(merged);
            setRecord(merged);
          } catch {
            if (cancel) return;
            const local = getStudioScanById(decodedId);
            if (local) {
              setRecord(local);
            } else {
              message.warning(
                lang === "zh" ? "未找到该扫描记录" : "Scan not found",
              );
              navigate("/studio/scans", { replace: true });
            }
          }
        } else {
          const local = getStudioScanById(decodedId);
          if (local) {
            setRecord(local);
          } else {
            message.warning(
              lang === "zh" ? "未找到该扫描记录" : "Scan not found",
            );
            navigate("/studio/scans", { replace: true });
          }
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    void run();
    return () => {
      cancel = true;
    };
  }, [decodedId, historyVersion, lang, navigate]);

  const [scanning, setScanning] = useState(false);
  const [scanStageLine, setScanStageLine] = useState("");
  const [scanProgressPercent, setScanProgressPercent] = useState(0);
  const [scanActiveStepIndex, setScanActiveStepIndex] = useState(0);
  const [scanProgressComplete, setScanProgressComplete] = useState(false);

  const verticalOptions = useMemo(
    () => [
      { value: "cosmetics" as const, label: t.studioVerticalCosmetic },
      { value: "pet_food" as const, label: t.studioVerticalFood },
      { value: "baby" as const, label: t.studioVerticalBaby },
      { value: "supplements" as const, label: t.studioVerticalSupplement },
    ],
    [t],
  );

  const verticalLabel = useMemo(() => {
    if (!record) return "";
    const opt = verticalOptions.find((o) => o.value === record.verticalKey);
    return opt?.label ?? "";
  }, [record, verticalOptions]);

  const handleRerunScan = useCallback(async () => {
    if (!record) return;
    const content = record.claimsText.trim();
    if (!content) {
      message.warning(t.studioNoClaims);
      return;
    }
    if (billingReady && !isUnlimited && reviewCredits <= 0) {
      Modal.confirm({
        title: t.outOfCredits,
        content: t.reviewUpsell,
        okText: t.upgradePlanNow,
        cancelText: t.later,
        onOk: () => navigate("/app/upgrade"),
      });
      return;
    }
    const category: ReviewCategory = verticalToCategory(
      record.verticalKey as VerticalKey,
    );
    setScanning(true);
    setScanProgressComplete(false);
    setScanActiveStepIndex(0);
    setScanProgressPercent(8);
    setScanStageLine(
      record.scanLang === "zh"
        ? "正在连接审查服务…"
        : "Connecting to compliance engine…",
    );
    try {
      const res = await scanClaims(content, record.scanLang, category, {
        productDisplayName: record.internalName.trim() || undefined,
        seriesId: record.seriesId,
        onProgress: (p) => {
          const stepCount = getScanProgressStepLabels(record.scanLang).length;
          setScanStageLine(p.label);
          setScanProgressPercent(p.percent);
          setScanActiveStepIndex(p.isComplete ? stepCount : p.index);
          setScanProgressComplete(Boolean(p.isComplete));
        },
      });
      applyFromScanResult(res.credit_balance);
      const at = Date.now();
      const scanDisplayId = res.task_id ?? newScanDisplayId();
      appendStudioScan({
        scanDisplayId,
        completedAt: at,
        internalName: record.internalName,
        taskId: res.task_id ?? scanDisplayId,
        verticalKey: record.verticalKey,
        scanLang: record.scanLang,
        claimsText: content,
        result: res,
        ...(record.seriesId ? { seriesId: record.seriesId } : {}),
      });
      if (res.task_id && record.seriesId) {
        try {
          await patchReviewTaskSeries(res.task_id, record.seriesId);
        } catch {
          message.warning(t.studioSeriesSyncFailed);
        }
      }
      navigate(`/studio/scans/${encodeURIComponent(scanDisplayId)}`, {
        replace: true,
        state: { studioDetailBack: "records" },
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        Modal.confirm({
          title: t.outOfCredits,
          content: t.reviewUpsell,
          okText: t.upgradePlanNow,
          cancelText: t.later,
          onOk: () => navigate("/app/upgrade"),
        });
        return;
      }
      const detail = e instanceof ApiError ? e.message : t.studioScanFailed;
      message.error(detail);
    } finally {
      setScanning(false);
      setScanStageLine("");
      setScanProgressPercent(0);
      setScanActiveStepIndex(0);
      setScanProgressComplete(false);
    }
  }, [
    navigate,
    record,
    t.studioNoClaims,
    t.studioScanFailed,
    t.studioSeriesSyncFailed,
    t.outOfCredits,
    t.reviewUpsell,
    t.upgradePlanNow,
    t.later,
    billingReady,
    isUnlimited,
    reviewCredits,
    applyFromScanResult,
  ]);

  if (loading) {
    return (
      <div
        className="studio-wb-records-page"
        style={{
          padding: 48,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!record) {
    return null;
  }

  const backState =
    (routerLocation.state as StudioDetailLocationState | null) ?? {};
  const backTo =
    backState.studioDetailBack === "new" ? "/studio/new" : "/studio/scans";

  return (
    <>
      <ScanProgressModal
        open={scanning}
        title={t.studioScanning}
        steps={getScanProgressStepLabels(record.scanLang)}
        activeStepIndex={scanActiveStepIndex}
        percent={scanProgressPercent}
        statusLine={scanStageLine || (lang === "zh" ? "准备中…" : "Preparing…")}
        isComplete={scanProgressComplete}
        variant={isDark ? "dark" : "light"}
      />
      <div className="studio-wb-records-page">
        <StudioScanResultDetail
          embedded
          result={record.result}
          lang={lang}
          t={t}
          isDark={isDark}
          finalizedAt={record.finalizedAt}
          onFinalizeComplete={() => setHistoryVersion((v) => v + 1)}
          creditsPill={
            billingReady ? (
              <div className="sentinel-credit-pill">
                {t.creditsLabel}: {reviewCredits.toLocaleString()}
              </div>
            ) : null
          }
          onBack={() => navigate(backTo)}
          backLabel={t.studioDetailBack}
          productTitle={
            record.internalName.trim() ||
            (lang === "zh" ? "未命名产品" : "Untitled product")
          }
          verticalLabel={verticalLabel}
          skuLabel={record.internalName.trim() || "—"}
          scannedAt={new Date(record.completedAt)}
          scanIdShort={record.scanDisplayId}
          seriesId={record.seriesId}
          backendTaskId={record.taskId}
          claimsText={record.claimsText}
          onToggleLang={() => toggleLang()}
          onToggleTheme={() => toggleTheme(!isDark)}
          onRerunScan={async () => {
            if (scanning) return;
            await handleRerunScan();
          }}
          onDiscard={() => navigate("/studio/new")}
          onBackToWorkspace={() => navigate("/app/dashboard")}
          onAddToCompare={() => {
            if (record.seriesId) {
              navigate(
                `/studio/compare?series=${encodeURIComponent(record.seriesId)}`,
              );
              return;
            }
            message.info(
              lang === "zh"
                ? "请先在发起扫描时选择「系列」，即可在「版本对比」中比较同一系列下的多次扫描。"
                : "Select a series when starting a scan to compare multiple runs under Version compare.",
            );
          }}
        />
      </div>
    </>
  );
}
